import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ScrollReveal } from "@/components/shared/ScrollReveal";
import { legalDocs } from "@/data/legalContent";
import { legalDocsEn } from "@/data/legalContentEn";
import SEO from "@/components/shared/SEO";
import { useLang } from "@/i18n/LanguageContext";

function Block({ block, index }) {
  if (block.h) {
    return (
      <h2
        id={`s-${index}`}
        data-testid={`legal-section-${index}`}
        className="font-display text-2xl md:text-3xl text-white leading-tight tracking-tight pt-10 mt-10 border-t border-white/10 scroll-mt-32"
      >
        {block.h}
      </h2>
    );
  }
  if (block.h3) {
    return (
      <h3 className="font-display text-lg md:text-xl text-[var(--dp-accent)] pt-6 mt-2">
        {block.h3}
      </h3>
    );
  }
  if (block.p) {
    return <p className="text-neutral-400 text-[15px] md:text-base leading-relaxed mt-5">{block.p}</p>;
  }
  if (block.ul) {
    return (
      <ul className="mt-5 space-y-3">
        {block.ul.map((item, i) => (
          <li key={i} className="flex gap-3 text-neutral-400 text-[15px] md:text-base leading-relaxed">
            <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-[var(--dp-accent)]" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.table) {
    return (
      <div className="mt-6 overflow-x-auto border border-white/10">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 bg-white/[0.04]">
              {block.table.head.map((h, i) => (
                <th key={i} className="px-4 py-3 font-mono-eyebrow text-neutral-400 font-normal">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.table.rows.map((row, i) => (
              <tr key={i} className="border-b border-white/5 last:border-0">
                {row.map((cell, j) => (
                  <td key={j} className="px-4 py-3 align-top text-neutral-400 leading-relaxed">
                    {typeof cell === "object" ? (
                      <a
                        href={cell.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[var(--dp-accent)] link-underline"
                      >
                        {cell.text}
                      </a>
                    ) : (
                      cell
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
}

export default function LegalPage({ docKey, forceLang }) {
  const { lang } = useLang();
  const docIt = legalDocs[docKey];
  const docEn = legalDocsEn[docKey];
  const isEn = forceLang === "en" || lang === "en";
  const doc = isEn ? { ...docIt, ...docEn, eyebrow: docIt.eyebrow } : docIt;
  const headings = doc.blocks.filter((b) => b.h);

  return (
    <main data-testid={`legal-page-${doc.slug}`} className="bg-black text-white">
      <SEO
        title={`${docIt.title} | Delta Pictures`}
        description={docIt.intro || `${docIt.title} — Delta Pictures S.r.l., gruppo editoriale e agenzia di comunicazione.`}
        noindex
      />
      <div className="max-w-[900px] mx-auto px-6 md:px-10 pt-36 md:pt-44 pb-28">
        <ScrollReveal>
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 font-mono-eyebrow text-neutral-500 hover:text-white transition-colors mb-10"
          >
            <ArrowLeft size={14} /> Home
          </Link>
          <p className="font-mono-eyebrow text-[var(--dp-accent)] mb-6">
            {isEn ? "Legal documents" : doc.eyebrow}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight">
            {doc.title}
          </h1>
          {doc.intro && (
            <p className="text-neutral-400 text-lg leading-relaxed mt-8 max-w-2xl">{doc.intro}</p>
          )}
        </ScrollReveal>

        {doc.showIndex && (
          <ScrollReveal delay={0.1}>
            <nav data-testid="legal-index" className="mt-14 border border-white/10 p-6 md:p-8">
              <p className="font-mono-eyebrow text-neutral-500 mb-5">{isEn ? "Index" : "Indice"}</p>
              <ol className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {headings.map((b) => {
                  const idx = doc.blocks.indexOf(b);
                  return (
                    <li key={idx}>
                      <a
                        href={`#s-${idx}`}
                        className="text-neutral-400 hover:text-white text-sm transition-colors leading-relaxed"
                      >
                        {b.h}
                      </a>
                    </li>
                  );
                })}
              </ol>
            </nav>
          </ScrollReveal>
        )}

        <div className="mt-6">
          {doc.blocks.map((b, i) => (
            <Block key={i} block={b} index={i} />
          ))}
        </div>
      </div>
    </main>
  );
}
